import { useNotificationStore } from "@/zustand/useNotificationStore";
import useUnsavedChangesStore from "@/zustand/useUnsavedChangesStore";
import { clearSentryUser } from "@utils/sentry";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLogout } from "./useAuth";

const useConfirmNavigation = (setActiveTab?: (tab: string) => void) => {
  const {
    pendingTab,
    setPendingTab,
    hasUnsavedChanges,
    pendingNavigation,
    setHasUnsavedChanges,
    setPendingNavigation,
  } = useUnsavedChangesStore();
  const { setShowUnsavedChangesNotification } = useNotificationStore();

  const { mutate: logout } = useLogout();

  const navigate = useNavigate();

  const guardedTabSelect = (tab: string) => {
    if (!setActiveTab) return;
    if (hasUnsavedChanges) {
      setShowUnsavedChangesNotification(true);
      setPendingTab(tab);
    } else {
      setActiveTab(tab);
    }
  };

  const guardedNavigate = (path: string) => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesNotification(true);
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    if (pendingTab && !hasUnsavedChanges && setActiveTab) {
      setTimeout(() => {
        setActiveTab(pendingTab);
        setPendingTab(null);
      }, 1000);
    }
  }, [pendingTab, hasUnsavedChanges, setPendingTab, setActiveTab]);

  useEffect(() => {
    if (
      pendingNavigation &&
      !hasUnsavedChanges &&
      pendingNavigation !== "/login"
    ) {
      setTimeout(() => {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }, 2000);
    }

    if (
      !hasUnsavedChanges &&
      pendingNavigation &&
      pendingNavigation === "/login"
    ) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        logout(
          { refresh: refreshToken },
          {
            onSuccess: (response) => {
              if (response) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                clearSentryUser();
                setTimeout(() => {
                  setPendingNavigation(null);
                  navigate("/login");
                }, 2000);
              }
            },
            onError: (error) => {
              console.error("Logout Error:", error);
            },
          }
        );
      }
    }
  }, [
    pendingNavigation,
    hasUnsavedChanges,
    setPendingNavigation,
    navigate,
    logout,
  ]);

  return {
    guardedTabSelect,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setShowUnsavedChangesNotification,
    guardedNavigate,
  };
};

export default useConfirmNavigation;
